import {
  useSalesServiceSalesInvoicesBrokerPayCreate,
  useSalesServiceSalesInvoicesSendPaymentLinkCreate,
  useSalesServiceSalesInvoicesStaffPayCreate,
  useSalesServiceSalesOrdersCheckoutCreate,
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  CascoPackage,
  InvoiceDetail,
  OrderInspectionStatus,
  OrderStatus,
  PaymentTypeStaff,
  PaymentTypeStaffBroker,
  Status9e7Enum
} from '@insurance/api/requests'
import {
  PayBrokerButton,
  PaymentExtendType,
  PaymentValue,
  PayStaffButton,
  RefundMoneyButton
} from '@insurance/components'
import { useAppNavigate } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { useSessionContext } from '@shared/hooks'
import {
  getFormatCurrency,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Modal } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { useTranslation } from 'react-i18next'

import { useCascoInsuranceValues } from './CascoInsuranceContext'
import {
  formProps,
  normalizeCommonData,
  normalizeModifyOrderData,
  normalizeOrderData,
  orderParentOrChildId,
  removeCascoValuesFromStorage
} from './utils'

const { confirm } = Modal

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const CascoInsuranceControls = ({ steps, onReadyToSaveData }: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { message } = App.useApp()
  const { broker } = useSessionContext()
  const { order, isEditable } = useCascoInsuranceValues()
  const {
    stepKey,
    priceKey,
    prevStepKey,
    orderKey,
    insurancePackageProps,
    isOnlineKey
  } = formProps(t)
  const step = Form.useWatch(stepKey.name, { preserve: true })
  const orderId = Form.useWatch(orderKey.name, { preserve: true })
  const isOnlineOrder = Form.useWatch(isOnlineKey.name, { preserve: true })
  const insurancePackage = Form.useWatch(insurancePackageProps.name, {
    preserve: true
  })
  const price = Form.useWatch(priceKey.name, { preserve: true })

  const navigate = useAppNavigate()

  const hasPrevStep = step > 0
  const isCalculateStep = step === 0
  const isReadyToApprove = step === steps - 1 && !!orderId
  const isPreparePersons = !isCalculateStep && !isReadyToApprove
  const isMiniPackage = insurancePackage === CascoPackage.MINI

  const status =
    step >= 1 || insurancePackage === CascoPackage.MINI
      ? OrderInspectionStatus.INSPECTION
      : OrderInspectionStatus.DATA_ENTRY

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance)
  }

  const handleChangeStep = (currentStep: number) => {
    formInstance.setFieldValue(stepKey.name, currentStep)
    formInstance.setFieldValue(prevStepKey.name, step)
    onReadyToSaveData?.()
  }

  const removeAllFromStorage = () => {
    removeCascoValuesFromStorage(order?.id)
    removeCascoValuesFromStorage(orderId)
  }

  const handlePrevStep = () => {
    const prevStep = step - 1

    if (isEditable) handleChangeStep(prevStep)
    else if (orderId)
      updateOrder({
        id: orderId,
        requestBody: {
          stage: prevStep,
          status: !prevStep ? OrderInspectionStatus.DATA_ENTRY : status
        }
      })
  }

  const { mutate: updateOrder, isPending: isUpdateOrderPending } =
    useSalesServiceSalesOrdersPartialUpdate({
      onSuccess: (data, variable) => {
        const newStep = variable?.requestBody?.stage || 0
        handleChangeStep(newStep)

        if (!newStep) {
          formInstance.setFieldValue(priceKey.name, undefined)

          return
        }

        formInstance.setFieldValue(priceKey.name, {
          currency: data?.price_data?.currency,
          amount: Number(Number(data?.price_data?.price)?.toFixed(2))
        })
        formInstance.setFieldValue(orderKey.name, data.id)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = { ...error, body: error?.body?.casco }
        handleFinishStepError(err)
      }
    })

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onSuccess: (data) => {
        if (data.order?.id)
          updateOrder({
            id: data.order.id,
            requestBody: { stage: step + 1, status }
          })
      },
      onError: handleFinishStepError
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue(priceKey.name, {
          currency: data?.price_data?.currency,
          amount: Number(Number(data?.price_data?.price)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: (error: AnyObject) => {
        const err = {
          ...error,
          body: { ...error?.body?.casco, ...error?.body }
        }
        handleFinishStepError(err)
      }
    })

  const {
    mutate: calculateModifyRefund,
    isPending: isCalculatingModifyRefundLoading
  } = useSalesServiceSalesOrdersModificationPriceCreate({
    onSuccess: (data) => {
      formInstance.setFieldValue(priceKey.name, {
        currency: data?.price_data?.currency,
        amount: Number(Number(data?.price_data?.price)?.toFixed(2))
      })
    },
    onError: handleFinishStepError
  })

  const { mutateAsync: modifyOrder, isPending: isModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onSuccess: (order) => {
        formInstance.setFieldValue(orderKey.name, order.id)
      },
      onError: handleFinishStepError
    })

  const { mutate: payBroker, isPending: isBrokerPayLoading } =
    useSalesServiceSalesInvoicesBrokerPayCreate({
      onSuccess: ({ invoice }) => {
        removeAllFromStorage()
        message.success(t('insurance_created_successfully'))
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: handleFinishStepError
    })

  const { mutate: pay, isPending: isPayLoading } =
    useSalesServiceSalesInvoicesStaffPayCreate({
      onSuccess: ({ invoice }) => {
        removeAllFromStorage()
        message.success(t('insurance_created_successfully'))
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: (error, variables) => {
        handleFinishStepError(error)

        const isPayCash =
          variables?.requestBody?.payment_type === PaymentTypeStaff.CASH
        const errorStatus = (error as { status?: number })?.status

        if (isPayCash && errorStatus !== 500) {
          confirm({
            title: t('error_payment'),
            content: t('error_payment_confirmation'),
            okText: t('pay_manually'),
            onOk: () => {
              pay({
                id: variables.id,
                requestBody: { ...variables.requestBody, force_manual: true }
              })
            }
          })
        }
      }
    })

  const { mutate: checkoutOrder, isPending: isCheckoutLoading } =
    useSalesServiceSalesOrdersCheckoutCreate({
      onError: handleFinishStepError
    })

  const { mutate: paymentLink, isPending: isPaymentLinkLoading } =
    useSalesServiceSalesInvoicesSendPaymentLinkCreate({
      onSuccess: () => {
        removeAllFromStorage()
        message.success(t('insurance_created_successfully'))
      },
      onError: handleFinishStepError
    })

  const handleInitBrokerOrderMini = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = await normalizeOrderData(values)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return
      payBroker({ id: invoice.id })
    }

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: 0,
          status: Status9e7Enum.DATA_ENTRY,
          casco: bodyValues
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder(
            {
              id,
              requestBody: {
                payment_type: PaymentTypeStaffBroker.BROKER_SETTLEMENT
              }
            },
            { onSuccess: handleSuccess }
          )
      }
    )
  }

  const handleInitStaffOrderMini = async (paymentValue: PaymentValue) => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = await normalizeOrderData(values)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return

      if (paymentValue.paymentType !== PaymentExtendType.ONLINE_PAYMENT)
        pay({
          id: invoice.id,
          requestBody: {
            payment_type: paymentValue.paymentType,
            cash_box: paymentValue.cashBox
          }
        })

      if (paymentValue.paymentType === PaymentExtendType.ONLINE_PAYMENT)
        paymentLink(
          { id: invoice.id },
          {
            onSuccess: () => {
              navigate(
                invoice.order.id
                  ? paths.orders.details(invoice.order.id)
                  : paths.salesPanel.root
              )
            }
          }
        )
    }

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: 0,
          status: Status9e7Enum.DATA_ENTRY,
          casco: bodyValues
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder({ id }, { onSuccess: handleSuccess })
      }
    )
  }

  const handleCalculate = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (order?.id) {
      const bodyValues = await normalizeModifyOrderData(values)

      if (
        orderParentOrChildId(order) &&
        (!order?.child || order?.child?.status === OrderStatus.CANCELED)
      )
        await modifyOrder({
          id: order?.id,
          requestBody: replaceEmptyWithUndefined({ ...bodyValues, status })
        })

      return calculateModify({
        id: order.id,
        requestBody: replaceEmptyWithUndefined(bodyValues)
      })
    }

    const bodyValues = await normalizeCommonData(values, !values?.order_id)

    if (values?.order_id)
      updateOrder({
        id: values.order_id,
        requestBody: {
          stage: step + 1,
          status,
          casco: replaceEmptyWithUndefined(bodyValues)
        }
      })
    else calculate({ code: ProductCode.CASCO, requestBody: bodyValues })
  }

  const handleReadyToReview = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const orderStatus = isOnlineOrder
      ? OrderInspectionStatus.INSPECTION
      : OrderInspectionStatus.OFFER_READY

    if (order?.id) {
      const bodyValues = await normalizeModifyOrderData(values)

      const id =
        order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
          ? order?.parent?.id || order.id
          : order.id

      return modifyOrder(
        {
          id,
          requestBody: replaceEmptyWithUndefined({
            ...bodyValues,
            status: orderStatus
          })
        },
        {
          onSuccess: (order) => {
            removeAllFromStorage()
            navigate(
              order.id ? paths.orders.details(order.id) : paths.salesPanel.root
            )
          }
        }
      )
    }

    const bodyValues = await normalizeOrderData(values)

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: step,
          status: orderStatus,
          casco: bodyValues
        }
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order?.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const handleCalculateRefund = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = await normalizeModifyOrderData(values)

    if (order?.id)
      calculateModifyRefund({ requestBody: bodyValues!, id: order?.id })
  }

  const handleOnlyModify = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) return

    const id =
      order.status === OrderStatus.REJECTED || !orderParentOrChildId(order)
        ? order?.parent?.id || order.id
        : order.id

    const bodyValues = await normalizeModifyOrderData(values)
    modifyOrder(
      {
        id,
        requestBody: replaceEmptyWithUndefined({
          ...bodyValues,
          status: OrderInspectionStatus.OFFER_READY
        })
      },
      {
        onSuccess: (order) => {
          removeAllFromStorage()
          navigate(
            order.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  const orderPrice = {
    amount: order?.full_price ? Number(order?.full_price) : 0
  }

  const canRefund = isEditable && orderPrice?.amount > price?.amount

  const payDirect = isMiniPackage && !isEditable

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button
          color="primary"
          variant="outlined"
          loading={isUpdateOrderPending}
          onClick={handlePrevStep}
        >
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={
            isCalculatingLoading ||
            isUpdateOrderPending ||
            isCalculatingModifyLoading
          }
          onClick={handleCalculate}
        >
          {t('calculate')}
        </Button>
      )}

      {isPreparePersons && (
        <Button
          type="primary"
          loading={
            isCalculatingLoading ||
            isUpdateOrderPending ||
            isCalculatingModifyLoading
          }
          onClick={handleCalculate}
        >
          {t('next_step')}
        </Button>
      )}

      {!canRefund && isReadyToApprove && !payDirect && (
        <Button
          type="primary"
          loading={isUpdateOrderPending || isModifyOrderLoading}
          onClick={handleReadyToReview}
        >
          {t(isOnlineOrder ? 'to_offer' : 'to_approve')}
        </Button>
      )}

      {canRefund && isReadyToApprove && !payDirect && (
        <RefundMoneyButton
          loading={isCalculatingModifyRefundLoading}
          disabledSelect
          onChangeRefund={handleCalculateRefund}
          onRefund={handleOnlyModify}
        />
      )}

      {isReadyToApprove && payDirect && !broker && (
        <PayStaffButton
          price={getFormatCurrency({ amount: price?.amount })}
          loading={
            isUpdateOrderPending ||
            isCheckoutLoading ||
            isPayLoading ||
            isPaymentLinkLoading
          }
          onPay={handleInitStaffOrderMini}
        />
      )}

      {isReadyToApprove && payDirect && broker && (
        <PayBrokerButton
          loading={
            isUpdateOrderPending || isCheckoutLoading || isBrokerPayLoading
          }
          onPay={handleInitBrokerOrderMini}
        />
      )}
    </Flex>
  )
}
